import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const LS_KEY = 'platzona_language';

/**
 * The global auth-service language endpoint is the single source of truth
 * for language preference across all platforms and the portal.
 * This URL is relative so it resolves via each app's Vite proxy to the auth service.
 */
const AUTH_LANG_URL = '/api/v1/auth/language';

interface UseLanguageSyncOptions {
  /**
   * Platform-specific language URL (used by the LanguageSelector in Settings pages).
   * When provided, language changes are ALSO saved to the platform backend.
   */
  backendUrl?: string;
  /** Returns the current auth Bearer token, or null when not logged in */
  getToken?: () => string | null;
  /**
   * Pass the current auth token as a value so the effect re-runs when the
   * user logs in or out.  When the token changes from null → string the hook
   * immediately fetches the user's saved language from the auth service and
   * applies it — keeping language in sync across all platforms and devices.
   */
  authToken?: string | null;
}

export function useLanguageSync({ backendUrl, getToken, authToken }: UseLanguageSyncOptions = {}) {
  const { i18n } = useTranslation();

  // On login (or mount with valid token), fetch the globally-stored language
  // preference from the auth service and apply it.  This ensures language is
  // consistent across all platforms and devices without requiring the user to
  // re-select their language on every new platform.
  useEffect(() => {
    if (!getToken) return;
    const token = getToken();
    if (!token) return;

    fetch(AUTH_LANG_URL, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then((data: { language?: string } | null) => {
        if (data?.language && data.language !== i18n.language) {
          i18n.changeLanguage(data.language);
          localStorage.setItem(LS_KEY, data.language);
        } else if (data && !data.language) {
          // Account has no stored preference yet (never used the language menu):
          // save the language the app is showing, so account emails (verification,
          // password reset) reach the user in that language. Explicit choices still win.
          const shown = i18n.resolvedLanguage || i18n.language;
          if (!shown || shown === 'cimode') return;
          fetch(AUTH_LANG_URL, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ language: shown }),
          }).catch(() => {/* ignore */});
        }
      })
      .catch(() => {/* ignore — auth service may be unreachable; localStorage detection still works */});
  // Re-run when user logs in (authToken changes from null → string).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const changeLanguage = useCallback(async (code: string) => {
    await i18n.changeLanguage(code);
    localStorage.setItem(LS_KEY, code);

    if (!getToken) return;
    const token = getToken();
    if (!token) return;

    // Save to the global auth-service endpoint — single source of truth for all platforms.
    fetch(AUTH_LANG_URL, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ language: code }),
    }).catch(() => {/* ignore */});

    // Also sync to the platform-specific backend when provided (Settings pages).
    if (backendUrl) {
      fetch(backendUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ language: code }),
      }).catch(() => {/* ignore */});
    }
  }, [i18n, backendUrl, getToken]);

  return { currentLanguage: i18n.language, changeLanguage };
}
